<div class="animated fadeIn">

  <!-- ═══════════════════════════════════════════ -->
  <!-- LOADING STATE -->
  <!-- ═══════════════════════════════════════════ -->
  @if (loadingStatus()) {
  <app-page-loading [absolute]="true"></app-page-loading>
  }

  <!-- ═══════════════════════════════════════════ -->
  <!-- NOT LINKED STATE -->
  <!-- ═══════════════════════════════════════════ -->
  @if (!loadingStatus() && !isLinked()) {
  <div class="kite-section-card">
    <div class="kite-section-title">
      <span>QuickBooks Connection</span>
    </div>
    <div class="mt-2 p-5 rounded-xl" style="background-color: #ecf3fd">
      <div class="text-center mx-auto">
        <i class="fa fa-link fa-2x"></i>
        <div class="mt-2">
          <h5 class="mb-1 text-dark">This Customer Doesn't Have a QuickBooks Connection</h5>
          <span class="text-muted">Search and connect this customer with their QuickBooks profile to view financial
            data.</span>
        </div>
        <app-button variant="primary" class="mt-4" type="button" (onClick)="openLinkModal()">
          <i class="fa fa-search mr-2" aria-hidden="true"></i> Find in QuickBooks
        </app-button>
      </div>
    </div>
  </div>
  }

  <!-- ═══════════════════════════════════════════ -->
  <!-- RE-AUTH REQUIRED BANNER -->
  <!-- ═══════════════════════════════════════════ -->
  @if (!loadingStatus() && qbNeedsReauth()) {
  <div class="kite-section-card">
    <div class="kite-section-title">
      <span>QuickBooks Connection</span>
    </div>
    <div class="mt-2 p-4 rounded d-flex align-items-center gap-3"
      style="background-color: #fff8e1; border: 1px solid #ffe082;">
      <i class="fa fa-exclamation-triangle fa-2x" style="color: #f59e0b; flex-shrink: 0;"></i>
      <div class="flex-grow-1">
        <strong style="color: #b45309;">QuickBooks Session Expired</strong>
        <p class="mb-1 text-muted small">Your QuickBooks authorization has expired. You need to reconnect to continue
          syncing financial data. Your existing data is still available.</p>
      </div>
      <app-button variant="primary" (onClick)="openLinkModal()">
        <i class="fa fa-refresh mr-1"></i> Reconnect
      </app-button>
    </div>
  </div>
  }

  <!-- ═══════════════════════════════════════════ -->
  <!-- LINKED STATE -->
  <!-- ═══════════════════════════════════════════ -->
  @if (!loadingStatus() && isLinked()) {

  <!-- Section 1: Connection Info -->
  <div class="kite-section-card">
    <div class="kite-section-title flex-wrap gap-2">
      <div class="d-flex align-items-center gap-2">
        <i class="fa fa-check-circle text-success"></i>
        <span>QuickBooks Connection</span>
      </div>
      <div class="section-actions d-flex align-items-center gap-2">
        <app-button size="sm" variant="blue-text" title="Update Data" (onClick)="syncData()" [loading]="loadingSyncData()">
          <i class="fa fa-sync mr-1"></i> Update Data
        </app-button>
        <app-button size="sm" variant="red-text" title="Disconnect" (onClick)="unlinkCustomer()" [loading]="loadingUnlink()">
          <i class="fa fa-unlink mr-1"></i> Disconnect
        </app-button>
      </div>
    </div>

    <div class="row align-items-end g-2 mt-1">
      <app-read-only-field label="QB Customer Name" class="col-xl-3 col-md-6 col-sm-6 mb-2"
        [value]="qbCustomerName()"></app-read-only-field>
      <app-read-only-field label="QB ID" class="col-xl-3 col-md-6 col-sm-6 mb-2"
        [value]="linkData()?.qbCustomerId"></app-read-only-field>
      <app-read-only-field label="Last Sync" class="col-xl-3 col-md-6 col-sm-6 mb-2"
        [value]="formatDate(linkData()?.lastSyncDate)"></app-read-only-field>
      <app-read-only-field label="Linked Date" class="col-xl-3 col-md-6 col-sm-6 mb-2"
        [value]="formatDate(linkData()?.createdAt)"></app-read-only-field>
    </div>
  </div>

  <!-- Section 2: Transaction History Grid -->
  <div class="kite-section-card">
    <div class="kite-section-title flex-wrap gap-2">
      <span>Transaction History <small class="text-muted fw-normal" style="font-size: 11px;">({{ transactions().length }} transactions)</small></span>
    </div>

    @if (financialData()) {
      <div class="row align-items-end g-2 mt-1 mb-2">
        <app-read-only-field label="Current Balance" class="col-xl-4 col-md-4 mb-2"
          [value]="formatCurrency(financialData()!.balance)"></app-read-only-field>
        <app-read-only-field label="Total Billed" class="col-xl-4 col-md-4 mb-2"
          [value]="formatCurrency(financialData()!.totalBilled)"></app-read-only-field>
        <app-read-only-field label="Total Paid" class="col-xl-4 col-md-4 mb-2"
          [value]="formatCurrency(financialData()!.totalPaid)"></app-read-only-field>
      </div>
    }

    <div class="w-100 position-relative">
      <app-kite-grid [columnDefs]="txnColumnDefs" [rowData]="transactions()" [totalRecords]="transactions().length"
        [pageSize]="transactions().length || 1" (cellClicked)="onTxnGridCellClicked($event)">
      </app-kite-grid>
    </div>
  </div>
  }
</div>


<!-- ═══════════════════════════════════════════ -->
<!-- LINK MODAL -->
<!-- ═══════════════════════════════════════════ -->
<ng-template #linkModal let-modal>
  <div class="modal-header border-bottom-0">
    <h5 class="modal-title">
      <i class="fa fa-search me-2"></i>Find in QuickBooks
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>

  <div class="modal-body px-4 pb-4 pt-0">
    <!-- Current Customer Address -->
    <div class="mb-4 mt-2">
      <app-read-only-field label="Customer Address in Kite"
        [value]="clientAddress() || 'No address available'"></app-read-only-field>
    </div>

    <label class="label">QuickBooks Matches</label>

    @if (loadingCandidates()) {
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-3 text-muted">Searching QuickBooks for matching customers...</p>
    </div>
    }

    @if (!loadingCandidates() && candidates().length === 0) {
    <div class="mt-2 p-4 rounded-xl" style="background-color: #f8f9fa; text-align: center;">
      <i class="fa fa-search fa-2x text-muted mb-2" style="display: block;"></i>
      <span class="text-muted">No customers in QuickBooks match this address.</span>
    </div>
    }

    @if (!loadingCandidates() && candidates().length > 0) {
    <div class="candidates-list mt-2" style="max-height: 400px; overflow-y: auto;">
      @for (candidate of candidates(); track candidate.qbCustomerId) {
      <div class="candidate-row p-3 mb-2 rounded border" style="cursor: pointer; border-color: #dee2e6;"
        (click)="linkCandidate(candidate)">
        <div class="d-flex justify-content-between align-items-center">
          <div class="flex-grow-1">
            <h6 class="mb-1 fw-semibold">{{ candidate.qbDisplayName }}</h6>
            <p class="text-muted mb-0 small">
              <i class="fa fa-map-marker-alt me-1"></i>{{ candidate.qbAddress || 'No address' }}
            </p>
          </div>
          <div class="d-flex align-items-center gap-3">
            <div class="rounded px-2 py-1 text-center text-white"
              [style.background-color]="getScoreColor(candidate.score)" style="min-width: 60px;">
              <span class="d-block fw-bold" style="font-size: 14px;">{{ (candidate.score * 100) | number:'1.0-0'
                }}%</span>
              <span class="d-block" style="font-size: 9px;">{{ getScoreLabel(candidate.score) }}</span>
            </div>
            <app-button variant="secondary" type="button">
              <i class="fa fa-link mr-1"></i> Link
            </app-button>
          </div>
        </div>
      </div>
      }
    </div>
    }
  </div>

  <div class="modal-footer">
    <app-button variant="secondary" (onClick)="closeModal()">Cancel</app-button>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════ -->
<!-- ═══════════════════════════════════════════ -->
<!-- DOCUMENT DETAIL MODAL -->
<!-- ═══════════════════════════════════════════ -->
<ng-template #docDetailModal let-modal>
  <app-personalized-dialog [loading]="loadingDocDetail()" [modal]="modal" (onClose)="closeDocModal()">
    <span dialog-title>
      <span>{{ selectedDoc()?.type }} Details</span>
      @if (selectedDoc()?.docNumber) {
        <span class="badge bg-white text-primary ms-2 font-normal" style="font-size: 13px;">#{{ selectedDoc()?.docNumber }}</span>
      }
    </span>

    <div dialog-content class="d-flex flex-column gap-3">
      @if (selectedDoc()) {

        <!-- ── Section 1: Transaction Overview ── -->
        <div class="kite-section-card mb-0">
          <div class="kite-section-title">
            <span>Transaction Overview</span>
          </div>
          <div class="row align-items-end g-2">
            <app-read-only-field
              variant="green"
              class="col-12 col-md-4 mb-2"
              [label]="selectedDoc()?.type === 'Payment' ? 'Payment Reference' : 'Document Number'"
              [value]="selectedDoc()?.docNumber ? '#' + selectedDoc()!.docNumber : (selectedDoc()?.paymentRefNum || '#' + selectedDoc()!.id)">
            </app-read-only-field>

            <app-read-only-field
              variant="gray"
              class="col-12 col-md-4 mb-2"
              label="Transaction Date"
              [value]="formatDate(selectedDoc()!.txnDate)">
            </app-read-only-field>

            <app-read-only-field
              variant="gray"
              class="col-12 col-md-4 mb-2"
              [label]="selectedDoc()?.type === 'Payment' ? 'Amount Paid' : 'Total Amount'"
              [value]="formatCurrency(selectedDoc()!.totalAmount)">
            </app-read-only-field>

            @if (selectedDoc()?.paymentMethod) {
              <app-read-only-field
                variant="gray"
                class="col-12 col-md-4 mb-2"
                label="Payment Method"
                [value]="selectedDoc()!.paymentMethod">
              </app-read-only-field>
            }

            @if (selectedDoc()?.depositAccount) {
              <app-read-only-field
                variant="gray"
                class="col-12 col-md-4 mb-2"
                label="Deposit Account"
                [value]="selectedDoc()!.depositAccount">
              </app-read-only-field>
            }

            @if (selectedDoc()?.balance !== undefined) {
              <app-read-only-field
                variant="gray"
                class="col-12 col-md-4 mb-2"
                label="Balance"
                [value]="formatCurrency(selectedDoc()!.balance!)">
              </app-read-only-field>
            }

            @if (selectedDoc()?.remainingCredit !== undefined) {
              <app-read-only-field
                variant="gray"
                class="col-12 col-md-4 mb-2"
                label="Remaining Credit"
                [value]="formatCurrency(selectedDoc()!.remainingCredit!)">
              </app-read-only-field>
            }

            @if (selectedDoc()?.dueDate) {
              <app-read-only-field
                variant="gray"
                class="col-12 col-md-4 mb-2"
                label="Due Date"
                [value]="formatDate(selectedDoc()!.dueDate!)">
              </app-read-only-field>
            }
          </div>
        </div>

        <!-- ── Section 2: Line Items (if present) ── -->
        @if (selectedDoc()!.lineItems?.length > 0) {
          <div class="kite-section-card mb-0">
            <div class="kite-section-title">
              <span>Line Items</span>
              <span class="text-xs font-normal text-slate-500 lowercase">({{ selectedDoc()!.lineItems.length }} {{ selectedDoc()!.lineItems.length === 1 ? 'item' : 'items' }})</span>
            </div>
            <div class="w-100 position-relative">
              <app-kite-grid
                [columnDefs]="lineItemsColumnDefs"
                [rowData]="selectedDoc()!.lineItems"
                [totalRecords]="selectedDoc()!.lineItems.length"
                [pageSize]="selectedDoc()!.lineItems.length || 1">
              </app-kite-grid>
            </div>
          </div>
        }

        <!-- ── Section 3: Linked Transactions / Applied Invoices ── -->
        @if (selectedDoc()!.linkedTxns?.length > 0) {
          <div class="kite-section-card mb-0">
            <div class="kite-section-title">
              <span>{{ selectedDoc()?.type === 'Payment' ? 'Applied to Invoices' : 'Payments Applied' }}</span>
              <span class="text-xs font-normal text-slate-500 lowercase">({{ selectedDoc()!.linkedTxns.length }} {{ selectedDoc()!.linkedTxns.length === 1 ? 'record' : 'records' }})</span>
            </div>
            <div class="d-flex flex-column gap-2">
              @for (lt of selectedDoc()!.linkedTxns; track lt.txnId) {
                <div class="p-3 bg-light border rounded-3 d-flex align-items-center justify-content-between gap-3">
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge badge-soft-primary px-2.5 py-1 text-xs font-semibold text-uppercase">{{ lt.txnType }}</span>
                    <span class="fw-semibold text-dark">{{ selectedDoc()?.type === 'Payment' ? 'Invoice #' : 'Payment #' }}{{ lt.txnId }}</span>
                    @if (lt.txnDate) {
                      <span class="text-muted small ms-2">
                        <i class="fa fa-calendar-o me-1"></i>{{ formatDate(lt.txnDate) }}
                      </span>
                    }
                  </div>
                  @if (lt.amount) {
                    <div class="text-end">
                      <span class="text-muted d-block text-uppercase fw-semibold" style="font-size: 10px; letter-spacing: 0.05em;">
                        {{ selectedDoc()?.type === 'Payment' ? 'Applied Amount' : 'Payment Amount' }}
                      </span>
                      <span class="fw-bold text-primary" style="font-size: 15px;">{{ formatCurrency(lt.amount) }}</span>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }

        <!-- ── Section 4: Additional Information & Notes ── -->
        @if (selectedDoc()?.customerMemo || selectedDoc()?.privateNote || selectedDoc()?.billAddr) {
          <div class="kite-section-card mb-0">
            <div class="kite-section-title">
              <span>Additional Information</span>
            </div>
            <div class="row g-2">
              @if (selectedDoc()?.billAddr) {
                <div class="col-12">
                  <app-read-only-field
                    label="Billing Address"
                    [value]="selectedDoc()!.billAddr"
                    [multiline]="true"
                    height="auto">
                  </app-read-only-field>
                </div>
              }
              @if (selectedDoc()?.customerMemo) {
                <div [ngClass]="selectedDoc()?.privateNote ? 'col-md-6 col-12' : 'col-12'">
                  <app-read-only-field
                    label="Customer Note"
                    [value]="selectedDoc()!.customerMemo"
                    [multiline]="true"
                    height="auto">
                  </app-read-only-field>
                </div>
              }
              @if (selectedDoc()?.privateNote) {
                <div [ngClass]="selectedDoc()?.customerMemo ? 'col-md-6 col-12' : 'col-12'">
                  <app-read-only-field
                    label="Internal Note"
                    [value]="selectedDoc()!.privateNote"
                    [multiline]="true"
                    height="auto">
                  </app-read-only-field>
                </div>
              }
            </div>
          </div>
        }

      }
    </div>

    <div dialog-actions class="d-flex justify-content-end gap-2 w-100">
      <app-button variant="secondary" btnClass="px-4" (onClick)="closeDocModal()">Close</app-button>
    </div>
  </app-personalized-dialog>
</ng-template>